<template>
  <div class="block block--gray maxw--inset">
    <h5 class="subheader text--center text--secondary">{{ data?.subheading }}</h5>
    <h2 class="heading--med text--bold text--center text--secondary mb-2">{{ data?.heading }}</h2>
    <form class="form" @submit.prevent="submit">
      <div class="form__row">
        <FormTextField label="Voornaam" name="firstname" class="field--w1-2" v-model="v$.formData.firstname.$model" :validator="v$.formData.firstname"/>
        <FormTextField label="Achternaam" name="lastname" class="field--w1-2" v-model="v$.formData.lastname.$model" :validator="v$.formData.lastname"/>
      </div>
      <div class="form__row">
        <FormTextField label="Telefoonnummer" name="phone" class="field--w1-2" v-model="v$.formData.phone.$model" :validator="v$.formData.phone"/>
        <FormTextField label="E-mailadres" name="email" class="field--w1-2" v-model="v$.formData.email.$model" :validator="v$.formData.email"/>
      </div>
      <div class="form__row">
        <FormTextareaField label="Wat wil je met ons delen?" name="message" v-model="formData.message" :rows="6"/>
      </div>
      <div class="form__row">
        <FormUploadField label="Upload CV" name="files" v-model="attachments" :extensions="acceptedFileTypes" :validator="v$.attachments" @file-error="onFileError"/>
      </div>
      <NuxtTurnstile v-model="token" v-if="!isDisabled"/>
      <div class="form__button_row">
        <p class="">{{ uploadText }} Bij het versturen ga je akkoord met ons <nuxt-link to="/privacy-beleid" class="link--blend">privacybeleid</nuxt-link></p>
        <button class="btn btn--accent" type="submit">
          Verzenden
          <Spinner v-if="isLoading"/>
        </button>
      </div>
      <p class="field__status text--center" v-if="!neutral" :class="feedbackClass">{{ feedbackMessage }}</p>
    </form>
  </div>
</template>

<script lang="ts" setup>
import useVuelidate from '@vuelidate/core';
import prettyBytes from 'pretty-bytes';
import { required, email, helpers, maxLength } from '@vuelidate/validators';
import type Form from '~/typings/strapi/components/form';

const props = defineProps({
  data: {
    type: Object as PropType<Form>,
  },
  name: {
    type: String as PropType<string>,
    default: 'contact-form',
  },
  pageId: {
    type: Number as PropType<number>,
    default: 0,
  },
  identifier: {
    type: String as PropType<string>,
    default: 'default',
  }
});


const gtm = useGtm();
const client = useStrapiClient();
const { token, isDisabled, verify } = useTurnstile();
const { isLoading, startLoading, stopLoading } = useLoading();
const { feedbackMessage, resetStatus, setStatus, handleSuccess, neutral, feedbackClass } = useSubmitFeedback(
  'Bericht verzonden',
  'Er is iets fout gegaan. Probeer het later opnieuw'
);

const acceptedFileTypes = [
  'pdf',
  'doc',
  'docx',
  'txt',
  'png',
  'jpeg',
];

const maxSize = 10 * 1000 * 1000; // 10MB
const maxFiles = 3;

const uploadText = computed(() => `
  * Toegestane bestandtype: ${acceptedFileTypes.join(', ')}. Max. bestandsgrootte: ${prettyBytes(maxSize)}.
  Max. aantal bestanden: ${maxFiles}.
`)

const baseData = {
  firstname: '',
  lastname: '',
  email: '',
  phone: '',
  message: '',
};

const attachments = ref<File[]>([]);

const formData = reactive({...baseData});
const $externalResults = ref({});

function onFileError (error: { name: string, message: string }) {
  v$.value.attachments.$touch();
  $externalResults.value = { attachments: [error.message] };
}

async function submit (e: Event) {
  resetStatus();

  if (!(await v$.value.$validate()) || isLoading.value) return;
  
  startLoading();

  try {
    if (!(await verify())) {
      setStatus(false);
      return;
    }

    const body = new FormData();

    // Append files
    attachments.value.forEach((file) => {
      body.append('files.attachments', file);
    });

    // Create copy of data without 
    const innerFormData = Object.fromEntries(Object.entries(formData).filter(([key]) => key !== 'attachments'));

    // Append form data
    body.append('data', JSON.stringify({
      formData: innerFormData,
      formName: props.name,
      identifier: props.identifier,
      idPage: props.pageId,
    }));

    await client('form-responses', {
      method: 'POST',
      body
    });

    // Send out a gtm trigger event
    gtm?.trackEvent({
      event: 'form_success',
      category: 'form_submit',
      action: 'submit',
      label: `${props.name} has been submitted successfully`,
      value: `${props.identifier}__${props.pageId}`,
    })

    handleSuccess();

    // Reset form
    v$.value.$reset();
    Object.assign(formData, baseData);
    attachments.value = [];
  } catch (e) {
    // @ts-ignore
    useBugsnag().notify(e);
    setStatus(false);
  } finally {
    stopLoading();
  }
}

const rules = computed(() => ({
  formData: {
    firstname: {
      required: helpers.withMessage('Dit veld is verplicht', required),
    },
    lastname: {
      required: helpers.withMessage('Dit veld is verplicht', required),
    },
    email: {
      required: helpers.withMessage('Dit veld is verplicht', required),
      email: helpers.withMessage('Dit is geen geldig e-mailadres', email),
    },
    phone: {
      required: helpers.withMessage('Dit veld is verplicht', required),
    },
  },
  attachments: {
    required: helpers.withMessage('U dient minstens 1 bestand te uploaden', required),
    maxFiles: helpers.withMessage(`Maximaal ${maxFiles} bestanden toegestaan`, maxLength(maxFiles)),
    maxSize: helpers.withMessage(`Maximale bestandsgrootte is ${prettyBytes(maxSize)}`, (value: File[]) => value.every((file) => file.size <= maxSize)),
  }
}));

const v$ = useVuelidate(rules, {formData, attachments}, { $externalResults });
</script>
